import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, User, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import PageTitle from '../components/PageTitle';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/admin/posts" />;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/admin/posts');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="Admin Login" />

      <div className="min-h-screen flex items-center justify-center bg-black px-4 py-12 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-church-green/5 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-church-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-church-green/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <Card className="w-full max-w-md bg-[#0a0a0a] border-white/10 shadow-2xl shadow-black/50 relative z-10">
          <CardHeader className="space-y-2 text-center pb-8 border-b border-white/5">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-church-green/10 rounded-full border border-church-green/20">
                <Lock className="h-8 w-8 text-church-green" />
              </div>
            </div>
            <CardTitle className="text-3xl font-heading font-bold text-white tracking-wide">Área Administrativa</CardTitle>
            <CardDescription className="text-gray-400 font-light">
              Acesso restrito para gerenciamento de conteúdo
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email
                </label>
                <div className="relative group">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 group-focus-within:text-church-green transition-colors" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-church-green/50 focus:ring-church-green/20"
                    placeholder="admin@exemplo.com"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Senha
                </label>
                <div className="relative group">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 group-focus-within:text-church-green transition-colors" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-church-green/50 focus:ring-church-green/20"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-church-green text-white hover:bg-church-green/90 font-medium text-lg shadow-lg shadow-church-green/20 border-none transition-all duration-300"
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Acessar Painel'}
              </Button>
            </form>

            <div className="mt-8 text-center pt-6 border-t border-white/5">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white hover:bg-white/5 gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar ao site
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Login;